package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.GroupManagers
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.model.WorkplaceGroup
import com.evoapps.evoshift.domain.model.WorkplaceGroups
import com.evoapps.evoshift.domain.usecase.GetWorkplaceUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.owner.GetWorkplaceGroupsUseCase
import com.evoapps.evoshift.domain.usecase.owner.SaveGroupManagersUseCase
import com.evoapps.evoshift.domain.usecase.owner.SaveWorkplaceGroupsUseCase
import com.evoapps.evoshift.domain.usecase.user.GetAssignableEmployeesUseCase
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.group_managers_saved
import evoshift.composeapp.generated.resources.workplace_groups_saved
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/**
 * SH-267: the owner's workplace groups ("Grupy lokali"): which of their workplaces share people
 * for shifts. [workplaces] are the workplaces the person owns (id to name, in their order).
 */
data class WorkplaceGroupsUiState(
    val isLoading: Boolean = true,
    val ownerId: String = "",
    val workplaces: Map<String, String> = emptyMap(),
    val groups: List<WorkplaceGroup> = emptyList(),
    /** SH-268: each owned workplace's active people (for the group managers' choice and names). */
    val teams: Map<String, List<User>> = emptyMap(),
    val isSaving: Boolean = false,
    val error: UiText? = null,
    val successMessage: UiText? = null
) {
    /** The owned workplaces in no group, in their order. */
    val ungrouped: List<String> get() = WorkplaceGroups.ungrouped(groups, workplaces.keys.toList())

    /** SH-268: the people of the owner's workplaces, by uid. */
    val people: Map<String, User> get() = teams.values.flatten().associateBy { it.uid }

    /** SH-268: who may be named a manager of [group] (people of its workplaces, by name). */
    fun candidatesFor(group: WorkplaceGroup): List<User> = GroupManagers.candidates(group, teams)
}

sealed interface WorkplaceGroupsEvent {
    data class AddGroup(val name: String) : WorkplaceGroupsEvent
    data class Rename(val groupId: String, val name: String) : WorkplaceGroupsEvent
    data class Delete(val groupId: String) : WorkplaceGroupsEvent
    /** [workplaceId] into [toGroupId] (null: into no group). */
    data class Move(val workplaceId: String, val toGroupId: String?) : WorkplaceGroupsEvent
    /** SH-268: [userId] manages group [groupId] at [permission] (added, or their level changed). */
    data class SetManager(val groupId: String, val userId: String, val permission: UserPermission) : WorkplaceGroupsEvent
    /** SH-268: [userId] no longer manages group [groupId]. */
    data class RemoveManager(val groupId: String, val userId: String) : WorkplaceGroupsEvent
    data object MessageShown : WorkplaceGroupsEvent
    data object Refresh : WorkplaceGroupsEvent
}

class WorkplaceGroupsViewModel(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val getWorkplaceUseCase: GetWorkplaceUseCase,
    private val getGroups: GetWorkplaceGroupsUseCase,
    private val saveGroups: SaveWorkplaceGroupsUseCase,
    /** SH-268: null in older wiring: the groups show no managers' section. */
    private val saveManagers: SaveGroupManagersUseCase? = null,
    private val getEmployees: GetAssignableEmployeesUseCase? = null
) : ViewModel() {

    /** SH-268: whether the group cards offer their managers (the screen's section). */
    val offersManagers: Boolean get() = saveManagers != null && getEmployees != null

    private val _uiState = MutableStateFlow(WorkplaceGroupsUiState())
    val uiState: StateFlow<WorkplaceGroupsUiState> = _uiState.asStateFlow()

    init {
        load()
    }

    fun onEvent(event: WorkplaceGroupsEvent) {
        when (event) {
            is WorkplaceGroupsEvent.AddGroup -> change { groups ->
                groups + WorkplaceGroup(id = "", ownerId = _uiState.value.ownerId, name = event.name.trim(), workplaceIds = emptyList())
            }
            is WorkplaceGroupsEvent.Rename -> change { groups ->
                groups.map { if (it.id == event.groupId) it.copy(name = event.name.trim()) else it }
            }
            is WorkplaceGroupsEvent.Delete -> change { groups -> groups.filterNot { it.id == event.groupId } }
            is WorkplaceGroupsEvent.Move -> {
                if (WorkplaceGroups.groupOf(_uiState.value.groups, event.workplaceId)?.id == event.toGroupId) return
                change { groups -> WorkplaceGroups.move(groups, event.workplaceId, event.toGroupId) }
            }
            is WorkplaceGroupsEvent.SetManager -> changeManagers(event.groupId) { GroupManagers.put(it, event.userId, event.permission) }
            is WorkplaceGroupsEvent.RemoveManager -> changeManagers(event.groupId) { GroupManagers.remove(it, event.userId) }
            WorkplaceGroupsEvent.MessageShown -> _uiState.update { it.copy(error = null, successMessage = null) }
            WorkplaceGroupsEvent.Refresh -> load()
        }
    }

    /**
     * SH-268: optimistic, as the layout: the group's new list of managers shows at once and is
     * saved whole; a refusal puts the old one back with the reason.
     */
    private fun changeManagers(groupId: String, edit: (WorkplaceGroup) -> WorkplaceGroup) {
        val save = saveManagers ?: return
        val state = _uiState.value
        if (state.isSaving || state.isLoading) return
        val group = state.groups.firstOrNull { it.id == groupId && it.id.isNotBlank() } ?: return
        val changed = edit(group)
        if (changed.managers == group.managers) return
        val before = state.groups
        _uiState.update { s -> s.copy(groups = s.groups.map { if (it.id == groupId) changed else it }, isSaving = true) }
        viewModelScope.launch {
            save(group, changed.managers).fold(
                onSuccess = { saved ->
                    _uiState.update { s ->
                        s.copy(
                            isSaving = false,
                            groups = s.groups.map { if (it.id == groupId) it.copy(managers = saved) else it },
                            successMessage = UiText.Resource(Res.string.group_managers_saved)
                        )
                    }
                },
                onFailure = { e -> _uiState.update { it.copy(isSaving = false, groups = before, error = e.toUiText()) } }
            )
        }
    }

    private fun load() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = it.groups.isEmpty() && it.workplaces.isEmpty()) }
            val user = getCurrentUserUseCase().first() ?: return@launch _uiState.update { it.copy(isLoading = false) }
            // The person's own workplaces only: a co-owner's OWNER engagement is not theirs to group.
            val workplaces = linkedMapOf<String, String>()
            user.engagements.values
                .filter { it.isActive && it.permission == UserPermission.OWNER }
                .forEach { engagement ->
                    getWorkplaceUseCase(engagement.workplaceId).onSuccess { wp ->
                        if (wp.ownerId == user.uid) workplaces[wp.id] = wp.name
                    }
                }
            _uiState.update { it.copy(ownerId = user.uid, workplaces = workplaces) }
            getGroups(user.uid).fold(
                onSuccess = { groups -> _uiState.update { it.copy(isLoading = false, groups = groups) } },
                onFailure = { e -> _uiState.update { it.copy(isLoading = false, error = e.toUiText()) } }
            )
            // SH-268: the people the managers are chosen from (and named by); a team that does not
            // read only leaves its people out of the choice.
            val employees = getEmployees ?: return@launch
            val teams = workplaces.keys.associateWith { wp -> employees(wp).getOrNull().orEmpty() }
            _uiState.update { it.copy(teams = teams) }
        }
    }

    /** Optimistic: the new grouping shows at once and is saved whole; a failure puts the old one back. */
    private fun change(edit: (List<WorkplaceGroup>) -> List<WorkplaceGroup>) {
        val state = _uiState.value
        if (state.isSaving || state.isLoading) return
        val before = state.groups
        val after = edit(before)
        _uiState.update { it.copy(groups = after, isSaving = true) }
        viewModelScope.launch {
            saveGroups(after, state.workplaces.keys).fold(
                onSuccess = { saved ->
                    _uiState.update {
                        it.copy(isSaving = false, groups = sorted(saved), successMessage = UiText.Resource(Res.string.workplace_groups_saved))
                    }
                },
                onFailure = { e -> _uiState.update { it.copy(isSaving = false, groups = before, error = e.toUiText()) } }
            )
        }
    }

    private fun sorted(groups: List<WorkplaceGroup>) =
        groups.sortedWith(compareByDescending<WorkplaceGroup> { it.isDefault }.thenBy { it.name.lowercase() })
}
